import { useDispatch, useSelector } from "react-redux";

import ruby from "@/design/colors/ruby";
import trueBlue from "@/design/colors/trueBlue";
import HeaderCard from "@/design/components/HeaderCard";
import SelectableCard from "@/design/components/SelectableCard";
import FormGroup from "@/design/forms/FormGroup";
import { setColorFilter } from "@/store/reducers/settings";
import { viEclipse, viMoon, viSun } from "@sippy-platform/valkyrie";

import { Box, Grid, Stack, useColorScheme } from "@mui/material";
import {
  amber,
  blue,
  blueGrey,
  cyan,
  deepPurple,
  green,
  grey,
  lightGreen,
  lime,
  purple,
  red,
  teal,
} from "@mui/material/colors";

export default function Personalization() {
  const dispatch = useDispatch();
  const { mode, setMode } = useColorScheme();

  const settings = useSelector((state) => state.settings);

  return (
    <Stack spacing={2}>
      <HeaderCard title="Personalization" sx={{ ".MuiCardHeader-title": { my: 0 } }} />
      <FormGroup title="Color theme">
        <Grid size={{ lg: 4, sm: 6, xs: 12 }}>
          <SelectableCard
            selected={mode === "system"}
            onClick={() => setMode("system")}
            icon={viEclipse}
            title="System"
            description="Follow your system's theme."
            img="/settings/system.png"
          />
        </Grid>
        <Grid size={{ lg: 4, sm: 6, xs: 12 }}>
          <SelectableCard
            selected={mode === "light"}
            onClick={() => setMode("light")}
            icon={viSun}
            title="Light"
            description="Use the light theme."
            img="/settings/light.png"
          />
        </Grid>
        <Grid size={{ lg: 4, sm: 6, xs: 12 }}>
          <SelectableCard
            selected={mode === "dark"}
            onClick={() => setMode("dark")}
            icon={viMoon}
            title="Dark"
            description="Use the dark theme."
            img="/settings/dark.png"
          />
        </Grid>
      </FormGroup>
      <FormGroup title="Color filter">
        <Grid size={12}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(min(10rem, 100%), 1fr))",
              gridAutoRows: "1fr",
              gap: 2,
            }}
          >
            <SelectableCard
              selected={settings.colorFilter === "default"}
              onClick={() => dispatch(setColorFilter("default"))}
              title="Default"
            >
              <Box sx={{ flexGrow: 1 }} />
              <Stack
                direction="row"
                sx={{
                  gap: 0.5,
                  mt: 3,
                }}
              >
                <Box sx={{ borderRadius: 0.5, width: 20, height: 20, bgcolor: green[500] }} />
                <Box sx={{ borderRadius: 0.5, width: 20, height: 20, bgcolor: lime[500] }} />
                <Box sx={{ borderRadius: 0.5, width: 20, height: 20, bgcolor: amber[500] }} />
                <Box sx={{ borderRadius: 0.5, width: 20, height: 20, bgcolor: red[500] }} />
                <Box sx={{ borderRadius: 0.5, width: 20, height: 20, bgcolor: grey[500] }} />
              </Stack>
            </SelectableCard>
            <SelectableCard
              selected={settings.colorFilter === "protanopia"}
              onClick={() => dispatch(setColorFilter("protanopia"))}
              title="Protanopia & Deuteranopia"
            >
              <Box sx={{ flexGrow: 1 }} />
              <Stack
                direction="row"
                sx={{
                  gap: 0.5,
                  mt: 3,
                }}
              >
                <Box sx={{ borderRadius: 0.5, width: 20, height: 20, bgcolor: purple[500] }} />
                <Box sx={{ borderRadius: 0.5, width: 20, height: 20, bgcolor: blue[500] }} />
                <Box sx={{ borderRadius: 0.5, width: 20, height: 20, bgcolor: teal[500] }} />
                <Box sx={{ borderRadius: 0.5, width: 20, height: 20, bgcolor: amber[500] }} />
                <Box sx={{ borderRadius: 0.5, width: 20, height: 20, bgcolor: grey[500] }} />
              </Stack>
            </SelectableCard>
            <SelectableCard
              selected={settings.colorFilter === "tritanopia"}
              onClick={() => dispatch(setColorFilter("tritanopia"))}
              title="Tritanopia"
            >
              <Box sx={{ flexGrow: 1 }} />
              <Stack
                direction="row"
                sx={{
                  gap: 0.5,
                  mt: 3,
                }}
              >
                <Box sx={{ borderRadius: 0.5, width: 20, height: 20, bgcolor: trueBlue[500] }} />
                <Box sx={{ borderRadius: 0.5, width: 20, height: 20, bgcolor: cyan[500] }} />
                <Box sx={{ borderRadius: 0.5, width: 20, height: 20, bgcolor: amber[500] }} />
                <Box sx={{ borderRadius: 0.5, width: 20, height: 20, bgcolor: ruby[500] }} />
                <Box sx={{ borderRadius: 0.5, width: 20, height: 20, bgcolor: grey[500] }} />
              </Stack>
            </SelectableCard>
            <SelectableCard
              selected={settings.colorFilter === "achromatopsia"}
              onClick={() => dispatch(setColorFilter("achromatopsia"))}
              title="Achromatopsia"
            >
              <Box sx={{ flexGrow: 1 }} />
              <Stack
                direction="row"
                sx={{
                  gap: 0.5,
                  mt: 3,
                }}
              >
                <Box sx={{ borderRadius: 0.5, width: 20, height: 20, bgcolor: lightGreen[400] }} />
                <Box sx={{ borderRadius: 0.5, width: 20, height: 20, bgcolor: cyan[400] }} />
                <Box sx={{ borderRadius: 0.5, width: 20, height: 20, bgcolor: blue[500] }} />
                <Box sx={{ borderRadius: 0.5, width: 20, height: 20, bgcolor: deepPurple[500] }} />
                <Box sx={{ borderRadius: 0.5, width: 20, height: 20, bgcolor: blueGrey[400] }} />
              </Stack>
            </SelectableCard>
          </Box>
        </Grid>
      </FormGroup>
    </Stack>
  );
}
